import {
  Container,
  Title,
  Text,
  Group,
  Stack,
  Card,
  Badge,
  SimpleGrid,
  ThemeIcon,
  Alert,
  Loader,
  Center,
  Table,
  Button,
  RingProgress,
  Progress,
  Divider,
  Code,
} from '@mantine/core';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  IconServer,
  IconDatabase,
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconTrash,
  IconRefresh,
  IconHeartbeat,
} from '@tabler/icons-react';
import { api } from '@/services/api';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { AutoRefreshControls } from '@/components/AutoRefreshControls';
import { useAutoRefresh } from '@/hooks/useAutoRefresh';

interface SystemHealth {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  uptime?: number;
  version?: string;
  dependencies?: {
    database: { status: string; responseTime?: number; error?: string };
    github: { status: string; responseTime?: number; error?: string };
    kubernetes: { status: string; responseTime?: number; error?: string };
    queue: { status: string; responseTime?: number; error?: string };
  };
  checks?: {
    database: { status: string; latency_ms?: number };
    github: { status: string; latency_ms?: number };
    kubernetes: { status: string; latency_ms?: number };
    queue: { status: string; depth?: number };
  };
}

interface StuckEnvironment {
  id: string;
  name: string;
  status: string;
  namespace: string;
  created_at: string;
  updated_at: string;
  user_github_login: string;
}

interface CleanupStats {
  total_cleanups: number;
  successful: number;
  failed: number;
  last_cleanup: string | null;
  avg_duration_ms: number;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'healthy':
      return 'green';
    case 'degraded':
      return 'yellow';
    case 'unhealthy':
      return 'red';
    default:
      return 'gray';
  }
};

const safeFormatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'Never';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return 'Invalid date';
  }
};

export function AdminStatsPage() {
  const navigate = useNavigate();
  const { enabled: autoRefresh, setEnabled: setAutoRefresh, refetchInterval: healthRefetchInterval } = useAutoRefresh({ 
    defaultEnabled: true, 
    interval: 10000 
  });
  // For other queries, use 30s interval
  const statsRefetchInterval = autoRefresh ? 30000 : false;

  const {
    data: healthData,
    isLoading: isLoadingHealth,
    error: healthError,
    refetch: refetchHealth,
  } = useQuery({
    queryKey: ['systemHealth'],
    queryFn: async () => await api.getSystemHealth(),
    refetchInterval: healthRefetchInterval, // 10s for health
  });

  const {
    data: stuckEnvsData,
    isLoading: isLoadingStuck,
    refetch: refetchStuck,
  } = useQuery({
    queryKey: ['stuckEnvironments'],
    queryFn: async () => await api.getStuckEnvironments(),
    refetchInterval: statsRefetchInterval, // 30s for stuck envs
  });

  const {
    data: cleanupStatsData,
    isLoading: isLoadingCleanup,
    refetch: refetchCleanup,
  } = useQuery({
    queryKey: ['cleanupStats'],
    queryFn: async () => await api.getCleanupStats(),
    refetchInterval: statsRefetchInterval,
  });

  const {
    data: envsData,
    isLoading: isLoadingEnvs,
  } = useQuery({
    queryKey: ['environments'],
    queryFn: async () => await api.listEnvironments({ limit: 1000 }),
    refetchInterval: statsRefetchInterval,
  });

  const {
    data: databasesData,
    isLoading: isLoadingDatabases,
  } = useQuery({
    queryKey: ['databases'],
    queryFn: async () => await api.listDatabases({ limit: 1000 }),
    refetchInterval: statsRefetchInterval,
  });

  const queryClient = useQueryClient();

  const healthRefreshMutation = useMutation({
    mutationFn: () => api.recheckServiceHealth(),
    onSuccess: () => {
      // Invalidate health cache after a short delay
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['systemHealth'] });
        refetchHealth();
      }, 2000);
    }
  });

  const health: SystemHealth | null = healthData?.data || healthData || null;
  const stuckEnvs: StuckEnvironment[] = stuckEnvsData?.data || [];
  const cleanupStats: CleanupStats | null = cleanupStatsData?.data || null;
  const environments = envsData?.items || [];
  const databases = databasesData?.items || [];

  // Normalize health data - support both /health/detailed (checks) and /health/deps (dependencies)
  const healthChecks = health?.checks || health?.dependencies;
  const getLatency = (check: any) => check?.latency_ms ?? check?.responseTime;

  // Map service names to display names
  const getServiceDisplayName = (serviceName: string): string => {
    const nameMap: Record<string, string> = {
      database: 'Database',
      github: 'GitHub API',
      kubernetes: 'Kubernetes',
      queue: 'Job Queue',
      db_cloner: 'DB Cloner',
    };
    return nameMap[serviceName] || serviceName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const handleManualRefresh = () => {
    refetchHealth();
    refetchStuck();
    refetchCleanup();
  };

  // Calculate environment stats
  const envStats = {
    total: environments.length,
    running: environments.filter((e: any) => e.status === 'running').length,
    pending: environments.filter((e: any) => e.status === 'pending').length,
    deploying: environments.filter((e: any) => e.status === 'deploying').length,
    failed: environments.filter((e: any) => e.status === 'failed').length,
  };

  // Calculate database stats
  const dbStats = {
    total: databases.length,
    active: databases.filter((d: any) => d.status === 'active').length,
    creating: databases.filter((d: any) => d.status === 'creating').length,
    failed: databases.filter((d: any) => d.status === 'failed').length,
  };

  const isLoading = isLoadingHealth || isLoadingStuck || isLoadingCleanup;

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="md" align="flex-end">
        <Stack gap={5}>
          <Title order={1}>Admin Statistics</Title>
          <Text c="dimmed">System health and usage statistics</Text>
        </Stack>
        <AutoRefreshControls
          enabled={autoRefresh}
          onEnabledChange={setAutoRefresh}
          onManualRefresh={handleManualRefresh}
          isRefreshing={isLoading}
        />
      </Group>

      {/* System Health Section - Optional */}
      <Stack gap="md" mb="xl">
        <Group justify="space-between" align="center">
          <Title order={2} size="h3">
            System Health
          </Title>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconHeartbeat size={14} />}
            onClick={() => healthRefreshMutation.mutate()}
            loading={healthRefreshMutation.isPending}
          >
            Refresh Health
          </Button>
        </Group>

        {healthError ? (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            title="Admin API Access Required"
            color="blue"
            variant="light"
          >
            <Text size="sm">
              System health monitoring requires admin-level API authentication. This data is available
              when accessing the system with an API key that has the <Code>health:detailed</Code> or{' '}
              <Code>admin</Code> scope.
            </Text>
            <Text size="sm" mt="xs" c="dimmed">
              The other statistics on this page (Usage, Cleanup, Stuck Environments) are still available.
            </Text>
          </Alert>
        ) : isLoadingHealth ? (
          <Center h={200}>
            <Loader size="lg" />
          </Center>
        ) : health ? (
          <>
            <Card withBorder radius="md" p="lg">
              <Group justify="space-between" mb="md">
                <Group>
                  <ThemeIcon
                    size="xl"
                    radius="md"
                    variant="light"
                    color={getStatusColor(health.status)}
                  >
                    <IconHeartbeat size={24} />
                  </ThemeIcon>
                  <div>
                    <Text size="sm" c="dimmed">
                      Overall Status
                    </Text>
                    <Text size="xl" fw={700} tt="capitalize">
                      {health.status}
                    </Text>
                  </div>
                </Group>
                {(health.uptime || health.version) && (
                  <Group>
                    {health.uptime && (
                      <div style={{ textAlign: 'right' }}>
                        <Text size="xs" c="dimmed">
                          Uptime
                        </Text>
                        <Text size="lg" fw={600}>
                          {Math.floor(health.uptime / 1000 / 60 / 60)}h{' '}
                          {Math.floor((health.uptime / 1000 / 60) % 60)}m
                        </Text>
                      </div>
                    )}
                    {health.version && (
                      <div style={{ textAlign: 'right' }}>
                        <Text size="xs" c="dimmed">
                          Version
                        </Text>
                        <Text size="lg" fw={600}>
                          {health.version}
                        </Text>
                      </div>
                    )}
                  </Group>
                )}
              </Group>

              <Divider my="md" />

              <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
                {healthChecks && Object.entries(healthChecks).map(([serviceName, check]: [string, any]) => (
                  <Card key={serviceName} withBorder radius="sm" p="md">
                    <Group justify="space-between" mb="xs">
                      <Text size="sm" fw={500}>
                        {getServiceDisplayName(serviceName)}
                      </Text>
                      <ThemeIcon
                        size="sm"
                        radius="xl"
                        variant="light"
                        color={getStatusColor(check.status)}
                      >
                        {check.status === 'healthy' ? (
                          <IconCheck size={12} />
                        ) : (
                          <IconX size={12} />
                        )}
                      </ThemeIcon>
                    </Group>
                    <Badge
                      color={getStatusColor(check.status)}
                      variant="light"
                      fullWidth
                    >
                      {check.status.toUpperCase()}
                    </Badge>
                    {(check.depth !== undefined || getLatency(check) !== undefined) && (
                      <Text size="xs" c="dimmed" mt="xs">
                        {check.depth !== undefined
                          ? `${check.depth} jobs`
                          : `${getLatency(check)}ms`}
                      </Text>
                    )}
                  </Card>
                ))}
              </SimpleGrid>
            </Card>
          </>
        ) : null}
      </Stack>

      {/* Usage Statistics */}
      <Stack gap="md">
        <Title order={2} size="h3">
          Usage Statistics
        </Title>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {/* Environments Stats */}
          <Card withBorder radius="md" p="lg">
            <Group justify="space-between" mb="md">
              <Group>
                <ThemeIcon size="lg" radius="md" variant="light" color="blue">
                  <IconServer size={20} />
                </ThemeIcon>
                <div>
                  <Text size="sm" c="dimmed">
                    Environments
                  </Text>
                  <Text size="xl" fw={700}>
                    {envStats.total}
                  </Text>
                </div>
              </Group>
              <RingProgress
                size={80}
                thickness={8}
                sections={[
                  {
                    value: (envStats.running / (envStats.total || 1)) * 100,
                    color: 'green',
                    tooltip: `${envStats.running} running`,
                  },
                  {
                    value: (envStats.deploying / (envStats.total || 1)) * 100,
                    color: 'yellow',
                    tooltip: `${envStats.deploying} deploying`,
                  },
                  {
                    value: (envStats.pending / (envStats.total || 1)) * 100,
                    color: 'blue',
                    tooltip: `${envStats.pending} pending`,
                  },
                  {
                    value: (envStats.failed / (envStats.total || 1)) * 100,
                    color: 'red',
                    tooltip: `${envStats.failed} failed`,
                  },
                ]}
              />
            </Group>

            <Stack gap="xs">
              <Group justify="space-between">
                <Group gap="xs">
                  <Badge color="green" variant="light">
                    Running
                  </Badge>
                  <Text size="sm">{envStats.running}</Text>
                </Group>
                <Progress
                  value={(envStats.running / (envStats.total || 1)) * 100}
                  color="green"
                  style={{ flex: 1, maxWidth: 100 }}
                />
              </Group>

              <Group justify="space-between">
                <Group gap="xs">
                  <Badge color="yellow" variant="light">
                    Deploying
                  </Badge>
                  <Text size="sm">{envStats.deploying}</Text>
                </Group>
                <Progress
                  value={(envStats.deploying / (envStats.total || 1)) * 100}
                  color="yellow"
                  style={{ flex: 1, maxWidth: 100 }}
                />
              </Group>

              <Group justify="space-between">
                <Group gap="xs">
                  <Badge color="blue" variant="light">
                    Pending
                  </Badge>
                  <Text size="sm">{envStats.pending}</Text>
                </Group>
                <Progress
                  value={(envStats.pending / (envStats.total || 1)) * 100}
                  color="blue"
                  style={{ flex: 1, maxWidth: 100 }}
                />
              </Group>

              {envStats.failed > 0 && (
                <Group justify="space-between">
                  <Group gap="xs">
                    <Badge color="red" variant="light">
                      Failed
                    </Badge>
                    <Text size="sm">{envStats.failed}</Text>
                  </Group>
                  <Progress
                    value={(envStats.failed / (envStats.total || 1)) * 100}
                    color="red"
                    style={{ flex: 1, maxWidth: 100 }}
                  />
                </Group>
              )}
            </Stack>
          </Card>

          {/* Databases Stats */}
          <Card withBorder radius="md" p="lg">
            <Group justify="space-between" mb="md">
              <Group>
                <ThemeIcon size="lg" radius="md" variant="light" color="cyan">
                  <IconDatabase size={20} />
                </ThemeIcon>
                <div>
                  <Text size="sm" c="dimmed">
                    Databases
                  </Text>
                  <Text size="xl" fw={700}>
                    {dbStats.total}
                  </Text>
                </div>
              </Group>
              <RingProgress
                size={80}
                thickness={8}
                sections={[
                  {
                    value: (dbStats.active / (dbStats.total || 1)) * 100,
                    color: 'green',
                    tooltip: `${dbStats.active} active`,
                  },
                  {
                    value: (dbStats.creating / (dbStats.total || 1)) * 100,
                    color: 'yellow',
                    tooltip: `${dbStats.creating} creating`,
                  },
                  {
                    value: (dbStats.failed / (dbStats.total || 1)) * 100,
                    color: 'red',
                    tooltip: `${dbStats.failed} failed`,
                  },
                ]}
              />
            </Group>

            <Stack gap="xs">
              <Group justify="space-between">
                <Group gap="xs">
                  <Badge color="green" variant="light">
                    Active
                  </Badge>
                  <Text size="sm">{dbStats.active}</Text>
                </Group>
                <Progress
                  value={(dbStats.active / (dbStats.total || 1)) * 100}
                  color="green"
                  style={{ flex: 1, maxWidth: 100 }}
                />
              </Group>

              <Group justify="space-between">
                <Group gap="xs">
                  <Badge color="yellow" variant="light">
                    Creating
                  </Badge>
                  <Text size="sm">{dbStats.creating}</Text>
                </Group>
                <Progress
                  value={(dbStats.creating / (dbStats.total || 1)) * 100}
                  color="yellow"
                  style={{ flex: 1, maxWidth: 100 }}
                />
              </Group>

              {dbStats.failed > 0 && (
                <Group justify="space-between">
                  <Group gap="xs">
                    <Badge color="red" variant="light">
                      Failed
                    </Badge>
                    <Text size="sm">{dbStats.failed}</Text>
                  </Group>
                  <Progress
                    value={(dbStats.failed / (dbStats.total || 1)) * 100}
                    color="red"
                    style={{ flex: 1, maxWidth: 100 }}
                  />
                </Group>
              )}
            </Stack>
          </Card>
        </SimpleGrid>

        {/* Cleanup Statistics */}
        <Title order={2} size="h3" mt="xl">
          Automatic Cleanup (Last 24h)
        </Title>

        {isLoadingCleanup ? (
          <Center h={100}>
            <Loader />
          </Center>
        ) : cleanupStats ? (
          <Card withBorder radius="md" p="lg">
            <Group justify="space-between">
              <Group>
                <ThemeIcon size="lg" radius="md" variant="light" color="orange">
                  <IconTrash size={20} />
                </ThemeIcon>
                <div>
                  <Text size="sm" c="dimmed">
                    Total Cleanups
                  </Text>
                  <Text size="xl" fw={700}>
                    {cleanupStats.total_cleanups}
                  </Text>
                </div>
              </Group>

              <SimpleGrid cols={3} spacing="lg">
                <div style={{ textAlign: 'center' }}>
                  <Text size="xs" c="dimmed">
                    Successful
                  </Text>
                  <Badge color="green" size="lg" variant="light">
                    {cleanupStats.successful}
                  </Badge>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Text size="xs" c="dimmed">
                    Failed
                  </Text>
                  <Badge color="red" size="lg" variant="light">
                    {cleanupStats.failed}
                  </Badge>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Text size="xs" c="dimmed">
                    Avg Duration
                  </Text>
                  <Badge color="blue" size="lg" variant="light">
                    {Math.round(cleanupStats.avg_duration_ms / 1000)}s
                  </Badge>
                </div>
              </SimpleGrid>
            </Group>

            <Text size="sm" c="dimmed" mt="md">
              Last cleanup: {safeFormatDate(cleanupStats.last_cleanup)}
            </Text>
          </Card>
        ) : null}

        {/* Stuck Environments */}
        <Title order={2} size="h3" mt="xl">
          Stuck Environments
        </Title>

        {isLoadingStuck ? (
          <Center h={100}>
            <Loader />
          </Center>
        ) : stuckEnvs.length > 0 ? (
          <Card withBorder radius="md" p={0}>
            <Alert
              icon={<IconAlertTriangle size={16} />}
              title="Attention Required"
              color="orange"
              m="md"
            >
              {stuckEnvs.length} environment{stuckEnvs.length > 1 ? 's are' : ' is'} stuck
              in pending or deploying state for an extended period.
            </Alert>

            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Namespace</Table.Th>
                  <Table.Th>User</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {stuckEnvs.map((env) => (
                  <Table.Tr key={env.id}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {env.name}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={env.status === 'pending' ? 'blue' : 'yellow'}
                        variant="light"
                      >
                        {env.status.toUpperCase()}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {env.namespace}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{env.user_github_login}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {safeFormatDate(env.created_at)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => navigate(`/environments/${env.id}`)}
                      >
                        View
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
        ) : (
          <Card withBorder p="xl" radius="md" ta="center">
            <Stack align="center">
              <ThemeIcon size={48} radius="xl" variant="light" color="green">
                <IconCheck size={24} />
              </ThemeIcon>
              <Title order={3}>All Clear!</Title>
              <Text c="dimmed">No stuck environments detected.</Text>
            </Stack>
          </Card>
        )}
      </Stack>
    </Container>
  );
}

